import { 
  MandalaChart, 
  MandalaCell, 
  MandalaCellLink,
  CreateMandalaData,
  UpdateMandalaData,
  CreateCellData,
  UpdateCellData,
  MandalaFilters,
  CreateCellLinkData
} from '../types/Mandala';
import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

class MandalaService {
  // Create a new mandala chart with 9 cells
  async createMandalaChart(chartData: CreateMandalaData): Promise<{ success: boolean; message?: string; id?: number; center_cell_id?: number }> {
    try {
      const response = await makeAuthenticatedRequest('/mandalas', {
        method: 'POST',
        body: JSON.stringify(chartData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating mandala chart:', error);
      throw error;
    }
  }

  // Get mandala chart by ID
  async getMandalaChartById(chartId: string): Promise<{ success: boolean; chart?: MandalaChart; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/${chartId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching mandala chart:', error);
      throw error;
    }
  }

  // Get all mandala charts for a user
  async getUserMandalaCharts(filters: MandalaFilters): Promise<{ success: boolean; charts?: MandalaChart[]; count?: number; message?: string }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/mandalas/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user mandala charts:', error);
      throw error;
    }
  }

  // Update a mandala chart
  async updateMandalaChart(updateData: UpdateMandalaData): Promise<{ success: boolean; message?: string }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/mandalas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating mandala chart:', error);
      throw error;
    }
  }

  // Delete a mandala chart
  async deleteMandalaChart(chartId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/${chartId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting mandala chart:', error);
      throw error;
    }
  }

  // Update a mandala cell
  async updateCell(chartId: string, cellId: string, updateData: Omit<UpdateCellData, 'id'>): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/${chartId}/cells/${cellId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating mandala cell:', error);
      throw error;
    }
  }

  // Link a cell to a goal
  async linkCellToGoal(cellId: string, goalId: number): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const linkData: CreateCellLinkData = {
        cell_id: parseInt(cellId),
        link_type: 'goal',
        link_id: goalId
      };
      
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/links`, {
        method: 'POST',
        body: JSON.stringify(linkData)
      });
      
      return response;
    } catch (error) {
      console.error('Error linking cell to goal:', error);
      throw error;
    }
  }

  // Link a cell to a habit
  async linkCellToHabit(cellId: string, habitId: number): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const linkData: CreateCellLinkData = {
        cell_id: parseInt(cellId),
        link_type: 'habit',
        link_id: habitId
      };
      
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/links`, {
        method: 'POST',
        body: JSON.stringify(linkData)
      });
      
      return response;
    } catch (error) {
      console.error('Error linking cell to habit:', error);
      throw error;
    }
  }

  // Link a cell to a task
  async linkCellToTask(cellId: string, taskId: number): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const linkData: CreateCellLinkData = {
        cell_id: parseInt(cellId),
        link_type: 'task',
        link_id: taskId
      };
      
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/links`, {
        method: 'POST',
        body: JSON.stringify(linkData)
      });
      
      return response;
    } catch (error) {
      console.error('Error linking cell to task:', error);
      throw error;
    }
  }

  // Remove a link from a cell
  async unlinkCell(cellId: string, linkId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/links/${linkId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error unlinking cell:', error);
      throw error;
    }
  }

  // Get all links for a cell
  async getCellLinks(cellId: string): Promise<{ success: boolean; links?: MandalaCellLink[]; count?: number; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/links`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching cell links:', error);
      throw error;
    }
  }

  // Calculate progress for a cell based on linked goals/habits/tasks
  async calculateCellProgress(cellId: string): Promise<{ 
    success: boolean; 
    progress?: number; 
    item_count?: number;
    details?: Array<{
      link_type: 'goal' | 'habit' | 'task';
      link_id: number;
      title: string;
      progress: number;
    }>;
    message?: string 
  }> {
    try {
      const response = await makeAuthenticatedRequest(`/mandalas/cells/${cellId}/progress`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error calculating cell progress:', error);
      throw error;
    }
  }

  // Helper method to convert API response to MandalaChart
  convertApiMandalaChart(apiChart: any): MandalaChart {
    return {
      id: apiChart.id,
      user_id: apiChart.user_id,
      title: apiChart.title,
      description: apiChart.description,
      center_cell_id: apiChart.center_cell_id,
      created_at: apiChart.created_at,
      updated_at: apiChart.updated_at,
      cells: apiChart.cells ? apiChart.cells.map((cell: any) => this.convertApiMandalaCell(cell)) : undefined
    };
  }

  // Helper method to convert API response to MandalaCell
  convertApiMandalaCell(apiCell: any): MandalaCell {
    return {
      id: apiCell.id,
      mandala_chart_id: apiCell.mandala_chart_id,
      position: apiCell.position,
      title: apiCell.title,
      description: apiCell.description,
      color: apiCell.color,
      progress: typeof apiCell.progress === 'string' ? parseFloat(apiCell.progress) : (apiCell.progress || 0),
      created_at: apiCell.created_at,
      updated_at: apiCell.updated_at,
      links: apiCell.links ? apiCell.links.map((link: any) => this.convertApiMandalaCellLink(link)) : undefined
    };
  }

  // Helper method to convert API response to MandalaCellLink
  convertApiMandalaCellLink(apiLink: any): MandalaCellLink {
    return {
      id: apiLink.id,
      cell_id: apiLink.cell_id,
      link_type: apiLink.link_type,
      link_id: apiLink.link_id,
      created_at: apiLink.created_at
    };
  }
}

export const mandalaService = new MandalaService();

